import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import bonsaiCache from "../services/bonsaiCache";

/**
 * Hook for using Bonsai data with automatic language translation
 * This is a compatibility hook that maintains the same interface as before
 */
const useBonsaiData = () => {
  const { i18n } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  // Load data for current language
  const loadData = useCallback(
    async (language = null) => {
      const targetLanguage = language || i18n.language;

      if (targetLanguage === currentLanguage && data.length > 0) {
        return; // Already loaded
      }

      setLoading(true);
      setError(null);

      try {
        const result = await bonsaiCache.getData(targetLanguage);
        setData(result || []);
        setCurrentLanguage(targetLanguage);
      } catch (err) {
        setError("Failed to load Bonsai data");
        console.error("Error loading Bonsai data:", err);
      } finally {
        setLoading(false);
      }
    },
    [i18n.language, currentLanguage, data.length]
  );

  // Load data when component mounts or language changes
  useEffect(() => {
    loadData();
  }, [i18n.language, loadData]);

  // Subscribe to cache updates
  useEffect(() => {
    const unsubscribe = bonsaiCache.subscribe((newData, language) => {
      if (language === i18n.language) {
        setData(newData || []);
        setCurrentLanguage(language);
        setLoading(false);
        setError(null);
      }
    });

    return unsubscribe;
  }, [i18n.language]);

  // Preload data for a specific language
  const preloadLanguage = useCallback((language) => {
    bonsaiCache.preload(language);
  }, []);

  // Clear cache for current language
  const clearCache = useCallback(
    (language = null) => {
      const targetLanguage = language || i18n.language;
      bonsaiCache.clear(targetLanguage);
      if (targetLanguage === currentLanguage) {
        setData([]);
      }
    },
    [i18n.language, currentLanguage]
  );

  // Check if data is available for current language
  const isDataAvailable = useCallback(
    (language = null) => {
      const targetLanguage = language || i18n.language;
      return bonsaiCache.isDataAvailable(targetLanguage);
    },
    [i18n.language]
  );

  // Check if currently loading for current language
  const isCurrentlyLoading = useCallback(
    (language = null) => {
      const targetLanguage = language || i18n.language;
      return bonsaiCache.isCurrentlyLoading(targetLanguage);
    },
    [i18n.language]
  );

  return {
    data,
    loading,
    error,
    currentLanguage,
    loadData,
    preloadLanguage,
    clearCache,
    isDataAvailable,
    isLoading: isCurrentlyLoading(),
  };
};

export default useBonsaiData;

// Export named function for backward compatibility
export const useBonsaiPreload = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Preload Bonsai data for current language
    bonsaiCache.preload(i18n.language);
  }, [i18n.language]);

  return i18n.language;
};
